import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { QueryFunctionContext } from '@tanstack/query-core';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Query } from '../op-types';

/** What a `queryFn` written OUTSIDE `createQueryKeys` reads of TanStack's context: the abort signal. */
type QueryContext = Pick<QueryFunctionContext, 'signal'>;

/**
 * The risk tab's reads, nested under `detail(id)._ctx` like every other entity-scoped query.
 * They sit in their own function because inlined they grew `createPortfolioQueries` from 117 to
 * 142 lines - across the 120-line band, on a function whose first line no risk change touches,
 * so the PR check never showed the alert and it would have surfaced on `main` instead.
 *
 * `as const` on every key is load-bearing: out here nothing types the literal as a TUPLE, and
 * an array-typed `queryKey` makes the factory drop `_ctx` from `detail(id)` for EVERY consumer -
 * while this package's own `tsc` stays green (only the apps read `_ctx`).
 */
function riskContextQueries(client: StoxlyClient, portfolioId: string) {
  return {
    // Cache-only read of the risk tab's AI narrative; `generateRiskAiAnalysis` writes it.
    riskAiAnalysis: (query?: Query<'portfolioCoreGetRiskAiAnalysis'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetRiskAiAnalysis({
          params: { id: portfolioId },
          queries: query,
          signal,
        }),
    }),
    // The user's risk thresholds for this portfolio (or the catalog defaults).
    riskLimits: {
      queryKey: null,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioRiskLimitsGet({ params: { id: portfolioId }, signal }),
    },
    // What the daily evaluator recorded: limit breaches / recoveries and the score history.
    riskAlerts: (query?: Query<'portfolioRiskAlertsGet'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioRiskAlertsGet({ params: { id: portfolioId }, queries: query, signal }),
    }),
    riskMetrics: (query?: Query<'portfolioCoreGetRiskMetrics'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetRiskMetrics({
          params: { id: portfolioId },
          queries: query,
          signal,
        }),
    }),
    riskModel: (query?: Query<'portfolioCoreGetRiskModel'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetRiskModel({
          params: { id: portfolioId },
          queries: query,
          signal,
        }),
    }),
    riskPairCorrelation: (query: Query<'portfolioCoreGetRiskPairCorrelation'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetRiskPairCorrelation({
          params: { id: portfolioId },
          queries: query,
          signal,
        }),
    }),
  };
}

/**
 * The user's own dated values for bonds no feed prices (`GET /portfolios/:id/valuations`).
 * Under `detail(id)._ctx` so the `portfolios` invalidation `position_valuations_set` declares
 * repaints an open marks list after the copilot sets a value. Its own function, and `as const`,
 * for the reasons `riskContextQueries` gives: inlined, `createPortfolioQueries` nears the
 * 120-line band.
 */
function valuationContextQueries(client: StoxlyClient, portfolioId: string) {
  return {
    valuations: (query?: Query<'positionValuationsList'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.positionValuationsList({ params: { id: portfolioId }, queries: query, signal }),
    }),
    // The daily value of ONE unit of a held bond (`GET /portfolios/:id/valuations/series`): its
    // holdings row's price chart, off the model series the row is valued by.
    valuationSeries: (query: Query<'positionValuationsSeries'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.positionValuationsSeries({ params: { id: portfolioId }, queries: query, signal }),
    }),
  };
}

/**
 * A model-valued bond's lifecycle in one book (`GET /portfolios/:id/treasury-bonds/:symbol` and its
 * `/redemption-quote`): the lots, the coupons and maturities due, what an early redemption pays;
 * and the terms its holder saved (`GET /portfolios/:id/bond-terms/:symbol`). Under `detail(id)._ctx`
 * so the `portfolios` invalidation `treasury_bonds_book` and `bond_terms_set` declare repaints an
 * open bond dialog. Its own function for the 120-line band `riskContextQueries` names.
 */
function treasuryContextQueries(client: StoxlyClient, portfolioId: string) {
  return {
    treasuryBond: (symbol: string) => ({
      queryKey: [symbol] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.treasuryBondsHolding({ params: { id: portfolioId, symbol }, signal }),
    }),
    treasuryRedemptionQuote: (symbol: string, query: Query<'treasuryBondsRedemptionQuote'>) => ({
      queryKey: [symbol, { query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.treasuryBondsRedemptionQuote({
          params: { id: portfolioId, symbol },
          queries: query,
          signal,
        }),
    }),
    bondTerms: (symbol: string) => ({
      queryKey: [symbol] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.bondTermsGet({ params: { id: portfolioId, symbol }, signal }),
    }),
  };
}

/**
 * TRADING ACTIVITY — the calendar and the "how often / how long" figures, for every book of the
 * user (`['portfolios', 'allTradingActivity']`, the /investments panel) and, under `detail(id)._ctx`,
 * for one book (the Transactions tab; a merged shell reads its members). Own functions for the
 * 120-line band reason `riskContextQueries` gives.
 */
function activityQueries(client: StoxlyClient) {
  return {
    allTradingActivity: (query?: Query<'portfolioCoreGetAllOrderActivity'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetAllOrderActivity({ queries: query, signal }),
    }),
  };
}

/**
 * The ACCOUNT's monthly returns — every book folded as one merge of them all
 * (`['portfolios', 'accountMonthlyReturns', { query }]`, the /investments returns card's "all
 * portfolios" scope). Its own function for the 120-line band reason `riskContextQueries` gives.
 */
function accountReturnsQueries(client: StoxlyClient) {
  return {
    accountMonthlyReturns: (query?: Query<'portfolioCoreGetAccountMonthlyReturns'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetAccountMonthlyReturns({ queries: query, signal }),
    }),
  };
}

/**
 * The ACCOUNT's cash day by day, every wallet once, in one currency
 * (`['portfolios', 'accountCashHistory', { query }]`, the /investments value chart's cash line,
 * #3677). Its own function for the 120-line band reason `riskContextQueries` gives.
 */
function accountCashQueries(client: StoxlyClient) {
  return {
    accountCashHistory: (query?: Query<'portfolioCoreGetAccountCashHistory'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetAccountCashHistory({ queries: query, signal }),
    }),
  };
}

/**
 * Where a trade — typed in a dialog, not yet saved — takes its wallet's cash, from its own day on
 * (`['portfolios', 'cashProjection', { query }]`): the buy dialog's non-blocking below-zero
 * warning. Its own function for the 120-line band reason `riskContextQueries` gives.
 */
function cashProjectionQueries(client: StoxlyClient) {
  return {
    cashProjection: (query: Query<'portfolioCoreGetCashProjection'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetCashProjection({ queries: query, signal }),
    }),
  };
}

function activityContextQueries(client: StoxlyClient, portfolioId: string) {
  return {
    tradingActivity: (query?: Query<'portfolioCoreGetOrderActivity'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetOrderActivity({
          params: { id: portfolioId },
          queries: query,
          signal,
        }),
    }),
  };
}

/**
 * The book's analysis reads under `detail(id)._ctx`: analytics, the AI insight, the snapshot, the
 * summary metrics and the dividend projection. This and the two helpers below hold every context
 * query `detail` used to declare inline - inline they made `detail` 90 lines and
 * `createPortfolioQueries` 118, both over CodeQL's 80-line god-function band (a nested
 * callback's lines count toward every function around it). `as const` for the reason
 * `riskContextQueries` gives.
 */
function analysisContextQueries(client: StoxlyClient, portfolioId: string) {
  return {
    analytics: (query?: Query<'portfolioCoreGetAnalytics'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetAnalytics({
          params: { id: portfolioId },
          queries: query,
          signal,
        }),
    }),
    insight: (query?: Query<'portfolioInsightsGetInsight'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioInsightsGetInsight({
          params: { id: portfolioId },
          queries: query,
          signal,
        }),
    }),
    snapshot: (query?: Query<'portfolioCoreGetSnapshot'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetSnapshot({
          params: { id: portfolioId },
          queries: query,
          signal,
        }),
    }),
    // The period-returns strip's benchmark: ONE benchmark's closes on the book's basis (its
    // currency, a net total return), the trailing year and a week (#2315 WYK-5).
    benchmarkCloses: (query: Query<'portfolioCoreGetBenchmarkCloses'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetBenchmarkCloses({
          params: { id: portfolioId },
          queries: query,
          signal,
        }),
    }),
    summaryMetrics: (query?: Query<'portfolioCoreGetSummaryMetrics'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetSummaryMetrics({
          params: { id: portfolioId },
          queries: query,
          signal,
        }),
    }),
    // Dividends-tab income projection (tier ladder; STOCK_DIVIDENDS-gated).
    dividendProjection: (query?: Query<'portfolioCoreGetDividendProjection'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetDividendProjection({
          params: { id: portfolioId },
          queries: query,
          signal,
        }),
    }),
  };
}

/** What the book holds, under `detail(id)._ctx`: open positions, their lots, CFDs, closed ones. */
function holdingContextQueries(client: StoxlyClient, portfolioId: string) {
  return {
    positions: (query: Query<'portfolioCoreGetPositions'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetPositions({
          params: { id: portfolioId },
          queries: query,
          signal,
        }),
    }),
    // A position's remaining open tax lots — the pickable units for a
    // SPECIFIC (user-chosen lot) sell in the sell dialog.
    positionLots: (query: Query<'portfolioCoreGetPositionLots'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetPositionLots({
          params: { id: portfolioId },
          queries: query,
          signal,
        }),
    }),
    // Open-CFD hints — dividend-equivalent evidence of open CFD positions the
    // statement import could not create. Nested under detail(id) so a
    // detail-prefix invalidation refreshes it alongside positions.
    cfdHints: {
      queryKey: null,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetCfdHints({ params: { id: portfolioId }, signal }),
    },
    // Onboarding review CFD facts (open contracts, closed P&L summary, hints).
    cfdReview: {
      queryKey: null,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetCfdReview({ params: { id: portfolioId }, signal }),
    },
    closedPositions: {
      queryKey: null,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetClosedPositions({ params: { id: portfolioId }, signal }),
    },
  };
}

/** The book's ledger, under `detail(id)._ctx`: its transactions, their export, the import checks. */
function ledgerContextQueries(client: StoxlyClient, portfolioId: string) {
  return {
    transactions: (query?: Query<'portfolioCoreGetTransactions'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreGetTransactions({
          params: { id: portfolioId },
          queries: query,
          signal,
        }),
    }),
    transactionsExport: (query?: Query<'portfolioCoreExportTransactions'>) => ({
      queryKey: [{ query }] as const,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreExportTransactions({
          params: { id: portfolioId },
          queries: query,
          signal,
        }),
    }),
    importPriceCheck: {
      queryKey: null,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioCoreImportPriceCheck({ params: { id: portfolioId }, signal }),
    },
    // Per-file answer keys of this portfolio's last import (statement totals vs. what landed).
    importReconciliation: {
      queryKey: null,
      queryFn: ({ signal }: QueryContext) =>
        client.portfolioImportReconciliation({ params: { portfolioId }, signal }),
    },
  };
}

export function createPortfolioQueries(client: StoxlyClient) {
  return createQueryKeys('portfolios', {
    ...activityQueries(client),
    ...accountReturnsQueries(client),
    ...accountCashQueries(client),
    ...cashProjectionQueries(client),
    // ['portfolios', 'heldStocks']
    heldStocks: {
      queryKey: null,
      queryFn: ({ signal }) => client.portfolioCoreGetHeldStocks({ signal }),
    },

    // ['portfolios', 'heldHoldings', { query }] — every open holding valued as total-metrics
    // values it (the share-of-account panel's denominator)
    heldHoldings: (query?: Query<'portfolioCoreGetHeldHoldings'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.portfolioCoreGetHeldHoldings({ queries: query, signal }),
    }),

    // ['portfolios', 'allUserShares']
    allUserShares: {
      queryKey: null,
      queryFn: ({ signal }) => client.portfolioCoreGetAllUserShares({ signal }),
    },

    // ['portfolios', 'sharesAnalytics', { query }]
    sharesAnalytics: (query?: Query<'portfolioCoreGetSharesAnalytics'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.portfolioCoreGetSharesAnalytics({ queries: query, signal }),
    }),

    // ['portfolios', 'portfolioSummaries', { query }]
    portfolioSummaries: (query?: Query<'portfolioCoreGetPortfolioSummaries'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) =>
        client.portfolioCoreGetPortfolioSummaries({ queries: query, signal }),
    }),

    // ['portfolios', 'totalMetrics', { query }]
    totalMetrics: (query?: Query<'portfolioCoreGetTotalMetrics'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.portfolioCoreGetTotalMetrics({ queries: query, signal }),
    }),

    // ['portfolios', 'allTransactions', { query }]
    // The cross-portfolio feed. Serves the SAME paginated transaction DTO as
    // `detail(id)._ctx.transactions` and — since #707 declared it on the handler —
    // is typed + runtime-validated exactly like it: consumers read
    // `Response<'portfolioCoreGetAllTransactions'>['items'][number]`, never a
    // hand-declared row shape.
    allTransactions: (query?: Query<'portfolioCoreGetAllTransactions'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.portfolioCoreGetAllTransactions({ queries: query, signal }),
    }),
    allTransactionsExport: (query?: Query<'portfolioCoreExportAllTransactions'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) =>
        client.portfolioCoreExportAllTransactions({ queries: query, signal }),
    }),

    // ['portfolios', 'detail', portfolioId, ...]
    detail: (portfolioId: string) => ({
      queryKey: [portfolioId],
      queryFn: ({ signal }) => client.portfolioCoreFindOne({ params: { id: portfolioId }, signal }),
      contextQueries: {
        ...analysisContextQueries(client, portfolioId),
        ...holdingContextQueries(client, portfolioId),
        ...ledgerContextQueries(client, portfolioId),
        ...riskContextQueries(client, portfolioId),
        ...valuationContextQueries(client, portfolioId),
        ...treasuryContextQueries(client, portfolioId),
        ...activityContextQueries(client, portfolioId),
      },
    }),

    // ['portfolios', 'health']
    //
    // Every ledger/moat problem detectable for the AUTHENTICATED USER, account-wide:
    // delisted holdings, positions with no acquisition cost, stalled imports, possible
    // duplicate transactions, stale price coverage and stale FX. Account-scoped, so it
    // takes no portfolio id and sits beside the other list-level entries rather than
    // under `detail(id)`.
    //
    // The route is `/portfolio-health`, NOT `/portfolios/health`: the latter would be
    // captured by `GET /portfolios/:id` with `id = 'health'`.
    //
    // It carries only the six classes the CLIENT cannot see for itself. The engine's
    // own refusals and the connection/wallet/book classes are folded on the client from
    // data the page already holds (`features/portfolio/logic/health/fold-issues.ts`) —
    // one producer per fact, so the panel and the cards can never disagree.
    health: {
      queryKey: null,
      queryFn: ({ signal }) => client.portfolioHealthGetHealth({ signal }),
    },
  });
}

export { createPortfolioMutations } from './portfolio-mutations';
